import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';

export default function Dashboard() {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    dashboardAPI.stats()
      .then((r) => setStats(r.data.data))
      .catch(() => setError('Failed to load dashboard stats.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner fullPage message="Loading dashboard…" />;
  if (error)   return <ErrorBanner message={error} />;

  const { employees, documents, audit } = stats;

  const VISA_CARDS = [
    { key:'opt',         label:'OPT',          color:'indigo',  icon:'🎓' },
    { key:'stem_opt',    label:'STEM OPT',      color:'violet',  icon:'🔬' },
    { key:'cpt',         label:'CPT',           color:'green',   icon:'📋' },
    { key:'h1b',         label:'H-1B',          color:'amber',   icon:'🏢' },
    { key:'h4_ead',      label:'H-4 EAD',       color:'pink',    icon:'💼' },
    { key:'gc',          label:'Green Card',    color:'emerald', icon:'🌿' },
    { key:'citizenship', label:'Citizenship',   color:'teal',    icon:'🗽' },
  ].filter((c) => employees[c.key] > 0 || true); // always show all

  return (
    <div className="space-y-6">

      {/* Total + visa category cards */}
      <section>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Employee Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Total Employees"
            value={employees.total}
            color="blue"
            icon="👥"
            onClick={() => navigate('/employees')}
          />
          {VISA_CARDS.map((c) => (
            <StatCard
              key={c.key}
              label={c.label}
              value={employees[c.key]}
              color={c.color}
              icon={c.icon}
              onClick={() => navigate(`/employees?category=${c.key.toUpperCase()}`)}
            />
          ))}
        </div>
      </section>

      {/* Document alert cards */}
      <section>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Document Alerts</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            label="Missing Documents"
            value={documents.missing}
            color="red"
            icon="⚠️"
            onClick={() => navigate('/audit')}
            urgent={documents.missing > 0}
          />
          <StatCard
            label="Expiring (30 days)"
            value={documents.expiring_soon}
            color="orange"
            icon="⏰"
            onClick={() => navigate('/alerts')}
            urgent={documents.expiring_soon > 0}
          />
          <StatCard
            label="Expired Documents"
            value={documents.expired}
            color="red"
            icon="❌"
            onClick={() => navigate('/alerts')}
            urgent={documents.expired > 0}
          />
        </div>
      </section>

      {/* Audit readiness */}
      <section>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Audit Readiness</h2>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex items-center gap-4">
              <div className={`w-20 h-20 rounded-full border-4 flex items-center justify-center text-2xl font-bold
                ${audit.percentage >= 80 ? 'border-green-500 text-green-600' :
                  audit.percentage >= 50 ? 'border-yellow-500 text-yellow-600' :
                  'border-red-500 text-red-600'}`}>
                {audit.percentage}%
              </div>
              <div>
                <p className="font-semibold text-slate-800 text-lg">Overall Readiness</p>
                <p className="text-slate-500 text-sm">{audit.ready} of {audit.total} employees fully compliant</p>
              </div>
            </div>
            <div className="flex-1 grid grid-cols-3 gap-4 md:ml-8">
              <MiniCard label="Ready"      value={audit.ready}      color="green" />
              <MiniCard label="Not Ready"  value={audit.not_ready}  color="red"   />
              <MiniCard label="Compliance" value={`${audit.percentage}%`} color="blue" />
            </div>
            <button onClick={() => navigate('/audit')}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg shrink-0">
              View Audit →
            </button>
          </div>
          <div className="mt-5">
            <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full progress-bar
                ${audit.percentage >= 80 ? 'bg-green-500' :
                  audit.percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                style={{ width: `${audit.percentage}%` }} />
            </div>
          </div>
        </div>
      </section>

      {/* Quick actions */}
      <section>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label:'Add Employee',   icon:'➕', to:'/employees/create' },
            { label:'View Employees', icon:'👥', to:'/employees'        },
            { label:'Check Alerts',   icon:'🔔', to:'/alerts'           },
            { label:'Audit Report',   icon:'📊', to:'/audit'            },
            { label:'Activity Logs',  icon:'📋', to:'/logs'             },
          ].map(({ label, icon, to }) => (
            <button key={to} onClick={() => navigate(to)}
              className="bg-white border border-slate-200 hover:border-blue-300 hover:shadow-sm text-slate-700 rounded-xl p-4 text-left transition-all">
              <span className="text-2xl">{icon}</span>
              <p className="mt-2 text-sm font-medium">{label}</p>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, color, icon, onClick, urgent }) {
  const colors = {
    blue:    'bg-blue-50    border-blue-200    text-blue-700',
    indigo:  'bg-indigo-50  border-indigo-200  text-indigo-700',
    violet:  'bg-violet-50  border-violet-200  text-violet-700',
    green:   'bg-green-50   border-green-200   text-green-700',
    amber:   'bg-amber-50   border-amber-200   text-amber-700',
    pink:    'bg-pink-50    border-pink-200    text-pink-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    teal:    'bg-teal-50    border-teal-200    text-teal-700',
    red:     'bg-red-50     border-red-200     text-red-700',
    orange:  'bg-orange-50  border-orange-200  text-orange-700',
  };
  return (
    <button onClick={onClick}
      className={`w-full text-left p-5 rounded-xl border-2 transition-all hover:shadow-md hover:-translate-y-0.5
        ${urgent ? 'animate-pulse' : ''} ${colors[color] || colors.blue}`}>
      <div className="text-3xl mb-2">{icon}</div>
      <div className="text-3xl font-bold">{value}</div>
      <div className="text-sm font-medium mt-1 opacity-80">{label}</div>
    </button>
  );
}

function MiniCard({ label, value, color }) {
  const c = { green:'text-green-600', red:'text-red-600', blue:'text-blue-600' };
  return (
    <div className="text-center">
      <div className={`text-2xl font-bold ${c[color]}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 text-center">
      <p className="font-medium">{message}</p>
    </div>
  );
}
